import React from 'react';
import cn from 'classnames';
import moment, { Moment } from 'moment';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  BadgeCell,
  BadgeCellComponentProps,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  DropdownButtonSkeleton,
  DropdownFilterOption,
  FilterItems,
  IconTextCell,
  IconTextCellComponentProps,
  IconTextCellSkeleton,
  MenuItemProps,
} from '@odin-labs/components';
import { ScheduleStatus } from 'apollo/generated/client-operations';
import { Jobsite } from 'graphql/client/useAvailableJobsites';
import {
  ensureNonEmptyItems,
  getInitialsForUser,
  getUserFullName,
  shouldOpenInNewTab,
} from 'utils';
import {
  BriefcaseIcon,
  CalendarDayIcon,
  EnvelopeCircleCheckIcon,
  EnvelopeIcon,
} from 'components/icons';
import { getPrettyFormattedShortDateTime } from 'utils/dates';
import { getDeveloperOptionsFromJobsites } from 'utils/filters';
import {
  Schedule,
  ScheduleColumn,
  ScheduleColumnName,
  ScheduleModalValue,
  SchedulesFilters,
  SchedulesFiltersOptions,
} from './types';

const scheduleStatusesOptions: DropdownFilterOption[] = [
  { value: ScheduleStatus.NotStarted, label: 'Not started' },
  { value: ScheduleStatus.InProgress, label: 'In progress' },
  { value: ScheduleStatus.Completed, label: 'Complete' },
];

const notificationsSentOptions: DropdownFilterOption[] = [
  {
    value: 'false',
    label: 'Not notified',
  },
  { value: 'true', label: 'Notified' },
];

const scheduleStatusBadgeColor: Record<
  ScheduleStatus,
  BadgeCellComponentProps['color']
> = {
  [ScheduleStatus.NotStarted]: 'gray',
  [ScheduleStatus.InProgress]: 'yellow',
  [ScheduleStatus.Completed]: 'green',
};
const scheduleStatusBadgeText: Record<ScheduleStatus, string> = {
  [ScheduleStatus.NotStarted]: 'Not started',
  [ScheduleStatus.InProgress]: 'In progress',
  [ScheduleStatus.Completed]: 'Completed',
};

export const getColumns = ({
  navigateToJobsiteSchedulesTab,
  completeSchedule,
  editSchedule,
  openModal,
}: {
  navigateToJobsiteSchedulesTab: (
    jobsiteId: Jobsite['jobsiteId'],
    scheduleDate: string,
    openInNewTab: boolean,
  ) => void;
  completeSchedule: (schedule: Schedule) => Promise<void>;
  editSchedule: (schedule: Schedule) => Promise<void>;
  openModal: (args: ScheduleModalValue) => void;
}): ScheduleColumn[] =>
  ensureNonEmptyItems<ScheduleColumn>([
    {
      id: ScheduleColumnName.Scheduler,
      Header: 'Scheduler',
      accessor: (schedule): string => getUserFullName(schedule.scheduler),
      Cell: AvatarCell,
      CellLoading: (): React.ReactElement => (
        <AvatarSkeleton size="xs" type="rounded" hideDetails />
      ),
      componentProps: (schedule: Schedule): AvatarCellComponentProps => {
        const { scheduler, jobsite } = schedule ?? {};
        const { profilePictureCropped } = scheduler ?? {};

        return {
          detail: jobsite?.developer?.name,
          objectFit: 'cover',
          placeholder: getInitialsForUser(scheduler),
          size: 'xs',
          src: profilePictureCropped?.downloadUrl,
        };
      },
    },
    {
      id: ScheduleColumnName.Jobsite,
      Header: 'Jobsite',
      accessor: (schedule: Schedule) => schedule?.jobsite?.name,
      Cell: IconTextCell,
      CellLoading: IconTextCellSkeleton,
      componentProps: (): IconTextCellComponentProps => ({
        icon: BriefcaseIcon,
        size: 'md',
      }),
    },
    {
      id: ScheduleColumnName.Date,
      Header: 'Date',
      accessor: 'date',
    },
    {
      id: ScheduleColumnName.ScheduleStatus,
      Header: 'Schedule status',
      accessor: 'status',
      Cell: BadgeCell,
      componentProps: (schedule: Schedule): BadgeCellComponentProps => ({
        text: scheduleStatusBadgeText[schedule?.status],
        color: scheduleStatusBadgeColor[schedule?.status],
        size: 'lg',
      }),
    },
    {
      id: ScheduleColumnName.NotificationStatus,
      Header: 'Notification status',
      accessor: (schedule: Schedule): string => {
        let text = 'Not available';
        if (schedule.status === ScheduleStatus.Completed) {
          text = schedule.notifiedAt
            ? `Notified ${getPrettyFormattedShortDateTime(schedule.notifiedAt)}`
            : 'Not notified';
        }
        return text;
      },
      Cell: IconTextCell,
      componentProps: (schedule: Schedule): IconTextCellComponentProps => {
        const { notifiedAt, status } = schedule;
        const stateClassName = cn({
          'odin-text-gray-300': status !== ScheduleStatus.Completed,
          'odin-text-gray-800':
            status === ScheduleStatus.Completed && !notifiedAt,
          'odin-text-green-600':
            status === ScheduleStatus.Completed && !!notifiedAt,
        });
        return {
          icon: notifiedAt ? EnvelopeCircleCheckIcon : EnvelopeIcon,
          size: 'md',
          variant: null,
          iconClassName: stateClassName,
          textClassName: stateClassName,
        };
      },
    },
    {
      id: ScheduleColumnName.Actions,
      Header: '',
      Cell: DropdownButtonCell,
      CellLoading: (): React.ReactElement => (
        <DropdownButtonSkeleton cellAlignment="right" />
      ),
      componentProps: (
        schedule: Schedule,
      ): DropdownButtonCellComponentProps => ({
        cellAlignment: 'right',
        menuItems: ensureNonEmptyItems<MenuItemProps>([
          {
            text: 'View schedule',
            theme: 'secondary',
            onClick: (event): void =>
              navigateToJobsiteSchedulesTab(
                schedule.jobsite.jobsiteId,
                schedule.date,
                shouldOpenInNewTab(event),
              ),
          },
          schedule.status === ScheduleStatus.InProgress && {
            text: 'Complete schedule',
            theme: 'secondary',
            onClick: (): void => {
              void completeSchedule(schedule);
            },
          },
          schedule.status === ScheduleStatus.Completed && {
            text: 'Edit schedule',
            theme: 'secondary',
            onClick: (): void => {
              void editSchedule(schedule);
            },
          },
          schedule.status !== ScheduleStatus.Completed &&
            // TODO: Temporary flag. Remove !InProgress flag when we allow for multiple shifts.
            schedule.status !== ScheduleStatus.InProgress && {
              text: 'Import schedule',
              theme: 'secondary',
              onClick: (): void => {
                openModal({
                  modalAction: 'importSchedule',
                  selectedSchedule: schedule,
                });
              },
            },
          schedule.status === ScheduleStatus.Completed && {
            text: 'Send notifications',
            theme: 'secondary',
            onClick: (): void => {
              openModal({
                modalAction: 'sendNotifications',
                selectedSchedule: schedule,
              });
            },
          },
        ]),
      }),
    },
  ]);

export const getFilterItems = ({
  jobsites,
  jobsiteIds,
  developerIds,
  filtersOptions,
  notificationsSent,
  scheduleStatuses,
  schedulerAccountIds,
  startDate,
  endDate,
  today,
}: {
  jobsites: Jobsite[];
  jobsiteIds: string[];
  developerIds: string[];
  filtersOptions: SchedulesFiltersOptions;
  notificationsSent?: string;
  scheduleStatuses: string[];
  schedulerAccountIds: string[];
  startDate: Moment | string;
  endDate: Moment | string;
  today: Moment | string;
}): FilterItems<SchedulesFilters> => ({
  dateRange: {
    header: 'Date',
    type: 'datePicker',
    defaultValue: { startDate, endDate },
    layout: cn('odin-col-span-full'),
    componentProps: {
      isRange: true,
    },
  },
  today: {
    header: 'Today',
    type: 'resetButton',
    componentProps: () => ({
      icon: CalendarDayIcon,
      filtersValues: {
        dateRange: {
          startDate: moment(today),
          endDate: moment(today),
        },
      },
    }),
  },
  developerIds: {
    header: 'Client',
    type: 'dropdown',
    defaultValue: developerIds,
    componentProps: {
      options: getDeveloperOptionsFromJobsites(jobsites),
      allowSearch: false,
    },
  },
  jobsiteIds: {
    header: 'Jobsite',
    type: 'dropdown',
    defaultValue: jobsiteIds,
    componentProps: {
      options:
        jobsites?.map(({ jobsiteId: value, name: label }) => ({
          value,
          label,
        })) ?? [],
    },
  },
  schedulerAccountIds: {
    header: 'Scheduler',
    type: 'dropdown',
    defaultValue: schedulerAccountIds,
    componentProps: {
      options: filtersOptions?.schedulerAccountIds ?? [],
      isMulti: true,
      allowSearch: false,
    },
  },
  scheduleStatuses: {
    header: 'Schedule Status',
    type: 'dropdown',
    defaultValue: scheduleStatuses,
    componentProps: {
      options: scheduleStatusesOptions,
      isMulti: true,
      allowSearch: false,
    },
  },
  notificationsSent: {
    header: 'Notifications',
    type: 'dropdown',
    defaultValue: notificationsSent,
    componentProps: {
      options: notificationsSentOptions,
      isMulti: false,
      allowSearch: false,
    },
  },
});
